import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { clone, isEmpty } from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { makeZeroTime } from 'src/app/sakp/util/common-utils';

@Component({
  selector: 'app-date-selector',
  templateUrl: './date-selector.component.html',
  styleUrls: ['./date-selector.component.scss']
})
export class DateSelectorComponent implements OnInit, OnDestroy {

  private isInitialized: boolean = false;

  @Input() range: boolean = false;
  @Input() showTime: boolean = false;
  @Input() showDayNavigation: boolean = false;
  @Input() title: string;
  @Input() minDate: Date;
  @Input() maxDaysRange: number;
  private initDate: Date | Date[];
  @Input() autoSubmit: boolean = false;

  @Input() set maxDate(date: Date) {
    this.maxRangeDay = this.permanentMaxDate = date;
  }

  maxRangeDay: Date;
  time1: Date;
  time2: Date;
  private previousValueSnapshot: string;
  private permanentMaxDate: Date;
  private dateValues: Date[] = [];

  notEmited: boolean = false;

  private calendarSubmit$ = new Subject<void>();
  private navSubmit$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  @Input()
  set date(dates: Date[] | Date) {
    if (this.isInitialized) this.applyExternalValue(dates);
    else this.initDate = dates;
  }

  /**
   * Model p-calendara. Musi być oddzielony od @Input date, bo zapis przez [(ngModel)] oznacza
   * wybór użytkownika (zmiana do wyemitowania), a zapis przez @Input - wartość narzuconą przez
   * rodzica, który już ją zna. Wspólny setter dla obu przypadków nie potrafiłby ich rozróżnić.
   */
  get calendarValue(): Date | Date[] {
    return this.dateValues.length > 1 ? this.dateValues : this.dateValues[0];
  }

  set calendarValue(dates: Date[] | Date) {
    this.setDateValues(dates);
    this.notEmited = true;
  }

  /**
   * Emit selected dates. If range enabled always emit list of dates.
   */
  @Output()
  valueSelected: EventEmitter<Date | Date[]> = new EventEmitter();

  ngOnInit(): void {
    const isArr = Array.isArray(this.initDate);
    const start = isArr ? this.initDate?.[0] : this.initDate;
    const end = isArr ? this.initDate?.[1] ?? this.initDate?.[0] : this.initDate;
    if (start && end && (end.valueOf() > this.maxRangeDay?.valueOf() || start.valueOf() < this.minDate?.valueOf())) {
      console.error('Incorrect init date');
      this.initDate = null;
    }
    this.isInitialized = true;
    if (!(this.initDate instanceof Date) && !(Array.isArray(this.initDate) && this.initDate.length > 0))
      this.initDate = makeZeroTime(new Date());
    this.date = this.initDate;

    this.calendarSubmit$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.isChange() && this.isValid()) this.submit();
    });
    this.navSubmit$.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe(() => {
      if (this.isChange() && this.isValid()) this.submit();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  submit(): void {
    this.previousValueSnapshot = this.makeSnaphot();
    if (this.range) {
      const dates: Date[] = this.createBetweenDates();
      this.valueSelected.emit(dates);
    } else {
      const date = this.mergeDates(clone(this.dateValues[0]), this.time1);
      this.valueSelected.emit(date);
    }
    this.notEmited = false;
  }

  private setDateValues(dates: Date[] | Date): void {
    if (Array.isArray(dates)) {
      this.dateValues = dates;
      if (this.dateValues[1] == null && this.maxDaysRange !== undefined) {
        const dynamicMax = moment(this.dateValues[0]).add(this.maxDaysRange, 'day').toDate();
        this.maxRangeDay = this.permanentMaxDate && dynamicMax > this.permanentMaxDate ? this.permanentMaxDate : dynamicMax;
      }
    } else this.dateValues = [dates];
  }

  /**
   * Wartość narzucona z zewnątrz jest już znana rodzicowi, więc staje się nowym punktem odniesienia
   * dla isChange(). Bez tego ponowny wybór dnia, na którym komponent się zainicjalizował, byłby
   * uznany za brak zmiany i nie wyemitowałby zdarzenia.
   */
  private applyExternalValue(dates: Date[] | Date): void {
    this.setDateValues(dates);
    this.time1 = this.dateValues[0];
    this.time2 = this.dateValues[1] ?? this.dateValues[0];
    this.previousValueSnapshot = this.makeSnaphot();
    this.notEmited = false;
  }

  private makeSnaphot(): string {
    return [...this.dateValues, this.time1, this.time2].toString();
  }

  private createBetweenDates(): Date[] {
    const first = this.mergeDates(clone(this.dateValues[0]), this.time1);
    const secondDate = this.dateValues[1] != null ? clone(this.dateValues[1]) : clone(this.dateValues[0]);
    const second = this.mergeDates(secondDate, this.time2);

    return [first, second];
  }

  private mergeDates(date: Date, time: Date): Date {
    date.setHours(time.getHours());
    date.setMinutes(time.getMinutes());
    date.setSeconds(time.getSeconds());
    return date;
  }

  isValid(): boolean {
    return !isEmpty(this.dateValues)
      && (!this.range || (this.dateValues[1] != null || this.time1 <= this.time2));
  }

  isChange(): boolean {
    return this.previousValueSnapshot !== this.makeSnaphot();
  }

  modelChange(): void {
    if (this.autoSubmit) {
      this.calendarSubmit$.next();
    }
  }

  goToDay(offset: number): void {
    const newDate = moment(this.dateValues[0]).add(offset, 'day').toDate();
    this.dateValues = [newDate];
    this.notEmited = true;
    this.navSubmit$.next();
  }

  get isPrevDayDisabled(): boolean {
    if (!this.dateValues[0]) return true;
    if (!this.minDate) return false;
    return moment(this.dateValues[0]).subtract(1, 'day').isBefore(moment(this.minDate), 'day');
  }

  get isNextDayDisabled(): boolean {
    if (!this.dateValues[0]) return true;
    if (!this.maxRangeDay) return false;
    return moment(this.dateValues[0]).add(1, 'day').isAfter(moment(this.maxRangeDay), 'day');
  }
}
